import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { getUserPermissions } from "src/utils/permissionsUtils";

const DEFAULT_PERCENT = 12;

export default function MegaspinSpecialChanceToggle({
  specialChance,
  onSave,
  isUpdating = false,
}) {
  const { isViewer } = getUserPermissions();
  const isEnabled = specialChance?.enabled === true;
  const savedPercent = specialChance?.percent ?? DEFAULT_PERCENT;
  const [percentInput, setPercentInput] = useState(String(savedPercent));

  useEffect(() => {
    setPercentInput(String(savedPercent));
  }, [savedPercent]);

  const parsedPercent = Number(percentInput);
  const isPercentValid =
    percentInput !== "" &&
    !Number.isNaN(parsedPercent) &&
    parsedPercent >= 0 &&
    parsedPercent <= 100;
  const isPercentDirty = isPercentValid && parsedPercent !== savedPercent;

  const handleToggle = (enabled) => {
    onSave({
      enabled,
      percent: isPercentValid ? parsedPercent : savedPercent,
    });
  };

  const handleSavePercent = () => {
    if (!isPercentValid) return;
    onSave({ enabled: isEnabled, percent: parsedPercent });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <Stack sx={{ p: 2 }} spacing={1.5}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BoltRoundedIcon
            sx={{ color: isEnabled ? "success.main" : "text.disabled" }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>
            Activate {savedPercent}% Special
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              color: isEnabled ? "success.dark" : "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {isEnabled ? "Enabled" : "Disabled"}
          </Typography>
          <Switch
            checked={isEnabled}
            onChange={(event) => handleToggle(event.target.checked)}
            disabled={isUpdating || isViewer}
            color={isEnabled ? "success" : "primary"}
          />
          {isUpdating && <CircularProgress size={16} thickness={6} />}
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
          <TextField
            label="Jackpot chance (%)"
            type="number"
            variant="standard"
            value={percentInput}
            onChange={(event) => setPercentInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleSavePercent();
            }}
            disabled={isUpdating || isViewer}
            error={!isPercentValid}
            helperText={
              !isPercentValid
                ? "Enter a number between 0 and 100"
                : isPercentDirty
                ? "Unsaved change"
                : " "
            }
            InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }}
            sx={{ width: 200 }}
          />
          {!isViewer && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSavePercent}
              disabled={isUpdating || !isPercentDirty}
              sx={{ mb: 2.5 }}
            >
              Save
            </Button>
          )}
        </Box>

        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          While enabled, the Mega Spin jackpot chance is fixed at{" "}
          <b>{savedPercent}%</b> for the current period and every period after
          a reset. When disabled, the jackpot chance follows the normal
          day-by-day schedule again, starting from Day 1 after the next reset.
          Turning the toggle on also saves whatever number is in the field.
        </Typography>
      </Stack>
    </Card>
  );
}
